/**
 * PRD.247 — Billing Item Payment Status table (patient-wide).
 *
 * The primary place to read item-level settlement: every invoice line item for
 * the patient, with Charge / Paid / Outstanding / Status derived strictly from
 * REAL payment allocations (invoice_payments.allocations). Unallocated
 * invoice-level payments are NOT spread across items — that would falsely imply
 * settlement the data can't support (PRD.247 §5.5 / §7.2); they're surfaced as
 * a lightweight notice instead. Invoice totals are unaffected (§7.4).
 */

import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  qInvoiceItemsByPatient,
  qPaymentsByPatient,
  aggregatePaymentAllocations,
  type PatientInvoiceItemRow,
  type InvoicePaymentRow,
} from '@helios/data-access';
import { phpFromCents } from '@helios/domain';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { itemChargeCents } from './payment-allocation-utils';

interface Props {
  patientId: string;
}

type PaymentStatus = 'paid' | 'partial' | 'unpaid';

interface Row {
  id: string;
  invoiceNo: string;
  name: string;
  chargeCents: number;
  paidCents: number;
  outstandingCents: number;
  status: PaymentStatus;
}

function statusOf(chargeCents: number, paidCents: number): PaymentStatus {
  // A zero-charge line has nothing owed → settled.
  if (chargeCents <= 0 || paidCents >= chargeCents) return 'paid';
  if (paidCents > 0) return 'partial';
  return 'unpaid';
}

const STATUS_STYLE: Record<PaymentStatus, { label: string; color: string; bg: string }> = {
  paid: {
    label: 'Paid',
    color: 'var(--color-success-text, #15803d)',
    bg: 'var(--color-success-bg, #f0fdf4)',
  },
  partial: {
    label: 'Partial',
    color: 'var(--color-warning-text, #92400e)',
    bg: 'var(--color-warning-bg, #fff7ed)',
  },
  unpaid: {
    label: 'Unpaid',
    color: 'var(--text-muted, #6b7280)',
    bg: 'var(--bg-surface-2, #f3f4f6)',
  },
};

export function PatientItemPaymentStatus({ patientId }: Props) {
  const fmt = useFormatMoney();
  const { data: items = [] } = usePowerSyncQuery<PatientInvoiceItemRow>(
    qInvoiceItemsByPatient(patientId),
  );
  const { data: payments = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    qPaymentsByPatient(patientId),
  );

  const { rows, unallocatedCents } = useMemo(() => {
    const knownIds = new Set(items.map((i) => i.id));
    const { allocatedByItem } = aggregatePaymentAllocations(payments, knownIds);

    // A fully-settled invoice unambiguously settles every item on it, so we
    // trust it even without item allocations (PRD.247 §5.5 — a reliable rule,
    // not a guess). Detect "fully paid" by AMOUNT (paid ≥ charge), not the
    // invoice status flag, which can go stale (e.g. after editing a payment).
    const chargeByInvoice = new Map<string, number>();
    for (const it of items) {
      if (!it.invoice_id) continue;
      chargeByInvoice.set(
        it.invoice_id,
        (chargeByInvoice.get(it.invoice_id) ?? 0) + itemChargeCents(it),
      );
    }
    const paidByInvoice = new Map<string, number>();
    for (const p of payments) {
      if (!p.invoice_id) continue;
      paidByInvoice.set(
        p.invoice_id,
        (paidByInvoice.get(p.invoice_id) ?? 0) + (p.amount_cents ?? 0),
      );
    }
    const fullyPaidInvoices = new Set<string>();
    for (const [invId, charge] of chargeByInvoice) {
      if (charge > 0 && (paidByInvoice.get(invId) ?? 0) >= charge) fullyPaidInvoices.add(invId);
    }

    const rows: Row[] = items.map((it) => {
      const chargeCents = itemChargeCents(it);
      const paidCents =
        it.invoice_id && fullyPaidInvoices.has(it.invoice_id)
          ? chargeCents
          : Math.min(chargeCents, allocatedByItem.get(it.id) ?? 0);
      return {
        id: it.id,
        invoiceNo: it.invoice_no || (it.invoice_id ? it.invoice_id.slice(0, 8) : '—'),
        name: it.service_name || it.description_snapshot || it.free_text_name || 'Item',
        chargeCents,
        paidCents,
        outstandingCents: Math.max(0, chargeCents - paidCents),
        status: statusOf(chargeCents, paidCents),
      };
    });

    // The "unallocated" notice should only flag genuine item-level ambiguity:
    // payments on NOT-fully-paid invoices (and patient-level payments) that
    // aren't tied to items. Payments on a fully-settled invoice need no
    // allocation — every item is already accounted for.
    const ambiguousPayments = payments.filter(
      (p) => !(p.invoice_id && fullyPaidInvoices.has(p.invoice_id)),
    );
    const { unallocatedCents } = aggregatePaymentAllocations(ambiguousPayments, knownIds);

    return { rows, unallocatedCents };
  }, [items, payments]);

  // No line items anywhere → nothing to summarize.
  if (rows.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-3)' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Item Payment Status
        </h3>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          Paid / outstanding per invoice item
        </span>
      </div>

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
        >
          <thead>
            <tr style={{ background: 'var(--bg-muted, #f9fafb)' }}>
              <Th>Invoice</Th>
              <Th>Item</Th>
              <Th align="right">Charge</Th>
              <Th align="right">Paid</Th>
              <Th align="right">Outstanding</Th>
              <Th align="center">Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const settled = r.status === 'paid';
              const s = STATUS_STYLE[r.status];
              return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <Td muted>{r.invoiceNo}</Td>
                  <Td>{r.name}</Td>
                  <Td align="right" mono>
                    {fmt(phpFromCents(r.chargeCents))}
                  </Td>
                  <Td align="right" mono>
                    <span style={{ color: 'var(--color-success-text)' }}>
                      {fmt(phpFromCents(r.paidCents))}
                    </span>
                  </Td>
                  <Td align="right" mono>
                    <span
                      style={{
                        color: settled ? 'var(--color-success-text)' : 'var(--color-warning-text)',
                        fontWeight: 600,
                      }}
                    >
                      {fmt(phpFromCents(r.outstandingCents))}
                    </span>
                  </Td>
                  <Td align="center">
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semi)',
                        color: s.color,
                        background: s.bg,
                      }}
                    >
                      {s.label}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {unallocatedCents > 0 && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {fmt(phpFromCents(unallocatedCents))} in payments isn’t allocated to specific items, so it
          isn’t reflected per item above. Click <strong>Allocate</strong> on a payment in the list
          above to assign it to items. Invoice totals remain authoritative.
        </p>
      )}
    </div>
  );
}

// ─── Cell helpers ───

function Th({ children, align }: { children: React.ReactNode; align?: 'right' | 'center' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
  muted,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
  mono?: boolean;
  muted?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        color: muted ? 'var(--text-muted)' : 'var(--text)',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
      }}
    >
      {children}
    </td>
  );
}
